import type { ColorFamily, ColorFamilySlug } from '@/types'

import { COLOR_FAMILIES } from '@/constants/colors'

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

export const parseHex = (value: string | null | undefined): [number, number, number] | null => {
    if (!value) return null

    const match = value.trim().match(HEX_PATTERN)

    if (!match) return null

    const hex = match[1].length === 3 ? match[1].split('').map(char => char + char).join('') : match[1]

    return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16)
    ]
}

// Buckets a hex color into one of the browse-by-color families by its hue, chroma and lightness.
export const getColorFamily = (value: string | null | undefined): ColorFamilySlug | null => {
    const rgb = parseHex(value)

    if (!rgb) return null

    const [r, g, b] = rgb.map(channel => channel / 255)
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const chroma = max - min
    const lightness = (max + min) / 2

    if (chroma < 0.08) {
        if (lightness > 0.8) return 'white'
        if (lightness < 0.2) return 'black'

        return 'gray'
    }

    if (lightness < 0.12) return 'black'

    let hue = 0

    if (max === r) hue = ((g - b) / chroma) % 6
    else if (max === g) hue = (b - r) / chroma + 2
    else hue = (r - g) / chroma + 4

    hue = (hue * 60 + 360) % 360

    if (hue >= 350 || hue < 15) return 'red'
    if (hue < 40) return 'orange'
    if (hue < 70) return 'yellow'
    if (hue < 160) return 'green'
    if (hue < 200) return 'teal'
    if (hue < 255) return 'blue'
    if (hue < 290) return 'purple'

    return 'pink'
}

export const isColorFamily = (value: unknown): value is ColorFamilySlug =>
    COLOR_FAMILIES.some(family => family.slug === value)

export const getColorFamilyInfo = (slug: string | null | undefined): ColorFamily | null =>
    COLOR_FAMILIES.find(family => family.slug === slug) ?? null

// A safe CSS color for inline placeholders, falling back to a neutral surface tone.
export const toCssColor = (value: string | null | undefined, fallback = '#1c1c22'): string => {
    const rgb = parseHex(value)

    if (!rgb) return fallback

    return `rgb(${rgb[0]} ${rgb[1]} ${rgb[2]})`
}
